import type { LinesheetLine } from "@nexxt/catalog-ui";
import { type CalculatedPrice, calculateLinePrices, type PricingSetup } from "@nexxt/contracts/catalog/rules";
import { type DraftState, parseDecimal } from "@nexxt/ui";

// The grid of the prices of a linesheet, as data (pattern.md §10): which price lists and currencies it offers, what a
// row shows for the one on display, and the overrides typed and not saved yet. No React here: the tab renders it.

export type LinePrice = LinesheetLine["prices"][number];

/** The prices on display: one price list in one currency at a time. */
export interface PricePair {
  priceList: string;
  currency: string;
}

/** `EXW-IT/EUR`: a pair in the URL and in the issues of the approval. */
export const pairKey = (pair: PricePair) => `${pair.priceList}/${pair.currency}`;

const samePair = (a: PricePair, b: PricePair) => a.priceList === b.priceList && a.currency === b.currency;

/**
 * The pairs to choose from: the ones the Price Setup makes due, in its order, then any other the lines have prices
 * for (a price list no longer active, or a user who does not read the setup).
 */
export function pricePairs(
  setup: Pick<PricingSetup, "lists"> | undefined,
  lines: readonly LinesheetLine[],
): PricePair[] {
  const pairs: PricePair[] = [];
  const add = (pair: PricePair) => {
    if (!pairs.some((p) => samePair(p, pair))) pairs.push({ priceList: pair.priceList, currency: pair.currency });
  };
  for (const list of setup?.lists ?? []) {
    for (const { currency } of list.currencies) add({ priceList: list.priceList, currency });
  }
  for (const line of lines) line.prices.forEach(add);
  return pairs;
}

/** The pair a key names among the ones on offer, or the first one. */
export function selectedPair(pairs: readonly PricePair[], key: string | undefined): PricePair | undefined {
  return pairs.find((p) => pairKey(p) === key) ?? pairs[0];
}

/** A line with its price for the pair on display, and what the calculation gives for it now (the preview). */
export interface PriceRow {
  line: LinesheetLine;
  /** Missing until the first calculation, unless the brand entered a price for this pair. */
  price: LinePrice | undefined;
  calculated: CalculatedPrice | undefined;
}

export function priceRows(
  lines: readonly LinesheetLine[],
  pair: PricePair,
  setup: PricingSetup | undefined,
): PriceRow[] {
  return lines.map((line) => ({
    line,
    price: line.prices.find((p) => samePair(p, pair)),
    calculated:
      setup &&
      calculateLinePrices(
        setup,
        line.prices.map((p) => ({
          priceList: p.priceList,
          currency: p.currency,
          brandPurchasePrice: p.brandPurchasePrice ?? null,
          brandWholesalePrice: p.brandWholesalePrice ?? null,
          brandRetailPrice: p.brandRetailPrice ?? null,
        })),
      ).find((p) => samePair(p, pair)),
  }));
}

export const priceViews = ["all", "missing", "overridden"] as const;
export type PriceView = (typeof priceViews)[number];

/** `missing`: what the approval would stop at, a line without a wholesale or a retail price for the pair. */
export function matchesView(row: PriceRow, view: PriceView): boolean {
  if (view === "missing") return !row.price?.wholesalePrice || !row.price.retailPrice;
  if (view === "overridden") return row.price?.isOverride === true;
  return true;
}

/** How far a price is from the calculated one, in percent; null when there is nothing to compare. */
export function priceDiff(value: string | null | undefined, calculated: string | null | undefined): number | null {
  if (!value || !calculated || Number(calculated) === 0) return null;
  return ((Number(value) - Number(calculated)) / Number(calculated)) * 100;
}

const priceFields = ["wholesalePrice", "retailPrice"] as const;
export type PriceField = (typeof priceFields)[number];

/** Overrides typed in the grid and not saved yet: the text as typed, and the version of the price it was typed on. */
export interface PriceDraft extends PricePair {
  lineId: string;
  version: number;
  wholesalePrice?: string;
  retailPrice?: string;
}

/** By line and pair: the screen keeps them until one save sends them all with one reason (FR-M3-04). */
export type PriceDrafts = ReadonlyMap<string, PriceDraft>;

const draftKey = (lineId: string, pair: PricePair) => `${lineId}|${pairKey(pair)}`;

export function draftOf(drafts: PriceDrafts, lineId: string, pair: PricePair): PriceDraft | undefined {
  return drafts.get(draftKey(lineId, pair));
}

/**
 * The drafts after a cell was left with `text`. An empty text, or the price the cell already has, is no change: the
 * draft of that field goes away. Anything else stays as typed, a text that is not a number too: the cell says so.
 */
export function setDraft(
  drafts: PriceDrafts,
  cell: { lineId: string; price: LinePrice; field: PriceField },
  text: string,
): PriceDrafts {
  const { lineId, price, field } = cell;
  const key = draftKey(lineId, price);
  const typed = text.trim();
  const parsed = parseDecimal(typed);
  const saved = price[field];
  const unchanged = typed === "" || (parsed !== null && saved != null && Number(parsed) === Number(saved));
  const current: PriceDraft = drafts.get(key) ?? {
    lineId,
    priceList: price.priceList,
    currency: price.currency,
    version: price.version,
  };
  const { [field]: _dropped, ...rest } = current;
  const next = new Map(drafts);
  if (!unchanged) next.set(key, { ...rest, version: price.version, [field]: typed });
  else if (priceFields.some((f) => f !== field && current[f] !== undefined)) next.set(key, rest);
  else next.delete(key);
  return next;
}

/** Whether a drafted cell can be saved; undefined for a cell without a draft. */
export function draftState(draft: PriceDraft, field: PriceField, price: LinePrice | undefined): DraftState | undefined {
  const typed = draft[field];
  if (typed === undefined) return undefined;
  if (parseDecimal(typed) === null) return "invalid";
  return price && price.version !== draft.version ? "stale" : "ok";
}

/** How many cells are drafted, and how many of them stop the save. */
export function draftSummary(drafts: PriceDrafts, lines: readonly LinesheetLine[]) {
  const summary = { changes: 0, invalid: 0, stale: 0 };
  for (const draft of drafts.values()) {
    const price = lines.find((l) => l.lineId === draft.lineId)?.prices.find((p) => samePair(p, draft));
    for (const field of priceFields) {
      const state = draftState(draft, field, price);
      if (!state) continue;
      summary.changes += 1;
      if (state !== "ok") summary[state] += 1;
    }
  }
  return summary;
}

/** The drafts as the overrides of one save, all with the same reason. */
export function overridePayload(drafts: PriceDrafts, reason: string) {
  return [...drafts.values()].map(({ wholesalePrice, retailPrice, ...key }) => ({
    ...key,
    ...(wholesalePrice !== undefined && { wholesalePrice: parseDecimal(wholesalePrice) ?? wholesalePrice }),
    ...(retailPrice !== undefined && { retailPrice: parseDecimal(retailPrice) ?? retailPrice }),
    reason,
  }));
}

/** A price of a line by its key, with a version: as a save answers it, and as clearing an override takes it. */
type PriceKey = PricePair & { lineId: string; version: number };

/**
 * What puts back a save of `drafts` (ADR-282), on the versions the save answered: a price that was an override is
 * set again as it was, with its reason; a calculated one loses its override, which calculates it again. Null when a
 * price of the save is not among `lines` as read before it, or was an override without the price drafted now.
 */
export function undoOverrides(
  drafts: PriceDrafts,
  lines: readonly LinesheetLine[],
  saved: readonly PriceKey[],
): { overrides: ReturnType<typeof overridePayload>; clears: PriceKey[] } | null {
  const overrides: ReturnType<typeof overridePayload> = [];
  const clears: PriceKey[] = [];
  for (const draft of drafts.values()) {
    const before = lines.find((l) => l.lineId === draft.lineId)?.prices.find((p) => samePair(p, draft));
    const after = saved.find((p) => p.lineId === draft.lineId && samePair(p, draft));
    if (!before || !after) return null;
    const key = { lineId: draft.lineId, priceList: draft.priceList, currency: draft.currency, version: after.version };
    if (!before.isOverride) {
      clears.push(key);
      continue;
    }
    const prices: { wholesalePrice?: string; retailPrice?: string } = {};
    for (const field of priceFields) {
      if (draft[field] === undefined) continue;
      const was = before[field];
      if (was == null) return null;
      prices[field] = was;
    }
    if (!before.overrideReason) return null;
    overrides.push({ ...key, ...prices, reason: before.overrideReason });
  }
  return { overrides, clears };
}
